import React from "react";
import { Button, ButtonGroup, Card, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { ReactComponent as MoreSvg } from "go-core/images/svg/actions/more.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import AccessTokenSessionService from "go-security/services/accessTokenSessionService";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { selectUser } from "go-security/services/users/selectors";
import Tag from "../../../../../../../../../../../components/Common/Tag/Tag";
import { ReactComponent as InstalledBySVG } from "../../../../../../../../../../../images/svg/apps/installed.svg";
import { ReactComponent as TagSVG } from "../../../../../../../../../../../images/svg/apps/tag.svg";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	ApplicationApi,
	ApplicationProvider,
	ApplicationTemplateApi,
} from "../../../../../../../../../../../services/Api/Organization/types";
import { AppState } from "../../../../../utils/types";

interface Props {
	installedApp?: ApplicationApi;
	setAppConfigForInstallationModal: React.Dispatch<React.SetStateAction<ApplicationTemplateApi | undefined>>;
	configApp: ApplicationTemplateApi;
	handleUpdateNewInstalledApp?: (app: ApplicationApi) => void;
	appState: AppState | undefined;
	routeProps?: RouteComponentProps;
	setAppState: React.Dispatch<React.SetStateAction<AppState | undefined>>;
	setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
	handleUninstall?: (app: ApplicationApi) => void;
	handleUpdate?: (app: ApplicationApi) => void;
	setShowParentModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

const AppInfoCard = ({
	installedApp,
	configApp,
	setAppConfigForInstallationModal,
	handleUpdateNewInstalledApp,
	appState,
	setAppState,
	setShowModal,
	handleUninstall,
	handleUpdate,
	routeProps,
	setShowParentModal,
}: Props) => {
	const { t } = useTranslation();
	const confirmation = useConfirmation();
	const { addSuccessFlash, addFlash } = useFlash();
	const isAdmin = useSelector(selectUser)?.admin;
	const organization = useSelector(selectOrganization);

	const onInstall = async () => {
		const accessToken = AccessTokenSessionService.get();

		switch (configApp.provider) {
			case ApplicationProvider.LOCATIONS:
			case ApplicationProvider.GOHOTELINTEGRATION:
			case ApplicationProvider.MEWS:
			case ApplicationProvider.HOTELLION:
			case ApplicationProvider.INFOR_STARLIGHT:
			case ApplicationProvider.HOLIDAY_PMS:
			case ApplicationProvider.HOTELGRAM:
			case ApplicationProvider.HOTRES:
			case ApplicationProvider.CHART:
			case ApplicationProvider.M_HOTEL:
			case ApplicationProvider.NFHOTEL:
			case ApplicationProvider.KWHOTEL:
			case ApplicationProvider.WOLT_DRIVE:
			case ApplicationProvider.BETASI:
			case ApplicationProvider.BETASI_V2:
				setShowModal(false);
				setAppConfigForInstallationModal(configApp);
				break;
			case ApplicationProvider.CALL_RECORDER: {
				const res = await apiOrganization.installApplicationPost(
					configApp.provider,
					accessToken ? accessToken : ""
				);
				setShowModal(false);
				setShowParentModal?.(false);
				handleUpdateNewInstalledApp && handleUpdateNewInstalledApp(res);
				break;
			}
			default:
				apiOrganization.installApplication(configApp.provider, accessToken ? accessToken : "");
		}
	};

	const onUninstall = async (app: ApplicationApi) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			await apiOrganization.removeApp(app);
			addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
			setShowModal(false);
			if (handleUninstall) {
				handleUninstall(app);
			}
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const enableApplication = async (app: ApplicationApi) => {
		try {
			const params: Record<string, any> = { include: "settings" };
			const res = await apiOrganization.enableApp(app, params);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			const res2 = await apiOrganization.getAppsConfig(res.id);
			setAppState({ config: res2, app: res });
			if (handleUpdate) {
				handleUpdate(res);
			}
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const resetApplication = async (app: ApplicationApi) => {
		try {
			const params: Record<string, any> = { include: "settings" };
			const res = await apiOrganization.resetApp(app, params);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			const res2 = await apiOrganization.getAppsConfig(res.id);
			setAppState({ config: res2, app: res });
			if (handleUpdate) {
				handleUpdate(res);
			}
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const deactivateApplication = async (app: ApplicationApi) => {
		try {
			const params: Record<string, any> = { include: "settings" };
			const res = await apiOrganization.deactivateApp(app, params);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			const res2 = await apiOrganization.getAppsConfig(res.id);
			setAppState({ config: res2, app: res });
			if (handleUpdate) {
				handleUpdate(res);
			}
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const syncApplication = async (app: ApplicationApi) => {
		try {
			await apiOrganization.syncApp(app);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const reinstallApplication = (app: ApplicationApi) => {
		const accessToken = AccessTokenSessionService.get();
		if (app.provider === ApplicationProvider.GOORDER || app.provider === ApplicationProvider.GOCRM) {
			apiOrganization.reinstallApplication(app, accessToken ? accessToken : "");
		} else {
			enableApplication(app);
		}
	};

	return (
		<Card className={"mb-3"}>
			<Card.Body>
				{!installedApp && (
					<Button variant={"primary btn-block mb-3"} onClick={() => onInstall()}>
						{t("common.action.install", { ns: "lib" })}
					</Button>
				)}
				{installedApp && appState?.app && (
					<div className={"d-flex mb-3"}>
						{appState?.app.status !== "ENABLED" && appState?.app.status !== "ERROR" && (
							<Button
								type={"button"}
								variant={"primary"}
								style={{ flex: 1, marginRight: "8px" }}
								onClick={() => enableApplication(installedApp)}
							>
								{t("common.action.activate", { ns: "lib" })}
							</Button>
						)}
						{appState?.app.status === "ENABLED" && (
							<Button
								variant={"light"}
								type={"button"}
								onClick={() => onUninstall(installedApp)}
								style={{
									color: "#DC3545",
									flex: 1,
									marginRight: "8px",
								}}
							>
								{t("common.word.uninstall")}
							</Button>
						)}
						{appState?.app.status === "ERROR" && appState?.app.provider === "GOORDER" && (
							<Button
								variant={"light"}
								type={"button"}
								onClick={() => reinstallApplication(installedApp)}
								style={{
									color: "#DC3545",
									flex: 1,
									marginRight: "8px",
								}}
							>
								{t("common.action.fix_error", { ns: "lib" })}
							</Button>
						)}
						{appState?.app.status === "ERROR" && appState?.app.provider === "LOCATIONS" && (
							<Button
								variant={"light"}
								type={"button"}
								onClick={() => enableApplication(installedApp)}
								style={{
									color: "#DC3545",
									flex: 1,
									marginRight: "8px",
								}}
							>
								{t("common.action.fix_error", { ns: "lib" })}
							</Button>
						)}
						<Dropdown as={ButtonGroup}>
							<Dropdown.Toggle as={MoreSvg} />
							<Dropdown.Menu>
								{appState?.app.status !== "DELETED" && appState?.app.status !== "ERROR" && (
									<Dropdown.Item href="#" onClick={() => syncApplication(installedApp)}>
										{t("common.action.sync", { ns: "lib" })}
									</Dropdown.Item>
								)}
								{appState?.app.status !== "DELETED" && (isAdmin || organization.support) && (
									<Dropdown.Item href="#" onClick={() => resetApplication(installedApp)}>
										{t("common.action.reset", { ns: "lib" })}
									</Dropdown.Item>
								)}
								<Dropdown.Item href="#" onClick={() => reinstallApplication(installedApp)}>
									{t("common.action.reinstall_channel")}
								</Dropdown.Item>
								{appState?.app.status === "ENABLED" && (
									<Dropdown.Item href="#" onClick={() => deactivateApplication(installedApp)}>
										{t("common.action.disable", { ns: "lib" })}
									</Dropdown.Item>
								)}
								{appState?.app && routeProps?.match?.url && (
									<Dropdown.Item as={Link} to={`${routeProps.match.url}/logs`}>
										{t("lib:common.word.logs")}
									</Dropdown.Item>
								)}
							</Dropdown.Menu>
						</Dropdown>
					</div>
				)}
				<div className="card-body-item">
					<InstalledBySVG />
					<div className="item-info">
						<span className="label">{t("modules.app.field.installed_by.title")}</span>
						<span>
							<div className={"action-ref"}>{configApp.owner}</div>
						</span>
					</div>
				</div>
				{configApp.tags && configApp.tags.length > 0 && (
					<div className="card-body-item">
						<TagSVG />
						<div className="item-info">
							<span className="label mb-1">{t("modules.app.field.categories.title")}</span>
							<div className={"app-tags"}>
								{configApp.tags?.map((tag, index) => {
									return <Tag key={index}>{tag}</Tag>;
								})}
							</div>
						</div>
					</div>
				)}
				{installedApp && configApp.documentation_link && (
					<a
						href={configApp.documentation_link}
						target={"_blank"}
						rel={"noreferrer"}
						className={"btn btn-add btn-block"}
					>
						{t("modules.app.field.docs.title")}
					</a>
				)}
			</Card.Body>
		</Card>
	);
};

export default AppInfoCard;
